// Global Error Types and Interfaces

export const ErrorSeverity = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical',
} as const;

export type ErrorSeverity = typeof ErrorSeverity[keyof typeof ErrorSeverity];

export const ErrorType = {
  NETWORK: 'network',
  VALIDATION: 'validation',
  AUTHENTICATION: 'authentication',
  AUTHORIZATION: 'authorization',
  NOT_FOUND: 'not_found',
  SERVER_ERROR: 'server_error',
  CLIENT_ERROR: 'client_error',
  UNKNOWN: 'unknown',
} as const;

export type ErrorType = typeof ErrorType[keyof typeof ErrorType];

export interface AppError {
  id: string;
  message: string;
  type: ErrorType;
  severity: ErrorSeverity;
  code?: string | number;
  details?: string;
  timestamp: Date;
  userMessage?: string;
  context?: Record<string, any>;
  stack?: string;
  originalError?: Error;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: AppError | null;
  errorId?: string;
}

export interface ErrorHandlerOptions {
  showToUser?: boolean;
  logToConsole?: boolean;
  severity?: ErrorSeverity;
  context?: Record<string, any>;
  userMessage?: string;
}

export interface ErrorServiceConfig {
  logToConsole: boolean;
  showUserNotifications: boolean;
  maxStoredErrors: number;
}

export interface NetworkError extends AppError {
  status?: number;
  statusText?: string;
  url?: string;
}

export interface ValidationError extends AppError {
  field?: string;
  validationRules?: string[];
}

// Error mapping for common HTTP status codes
export const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Bad request - please check your input',
  401: 'Authentication required - please log in',
  403: 'Access denied - you don\'t have permission',
  404: 'Resource not found',
  408: 'Request timeout - please try again',
  409: 'Conflict - resource already exists',
  422: 'Validation failed - please check your input',
  429: 'Too many requests - please try again later',
  500: 'Internal server error - please try again later',
  502: 'Service temporarily unavailable',
  503: 'Service temporarily unavailable',
  504: 'Request timeout - please try again',
};

// User-friendly error messages
export const USER_ERROR_MESSAGES: Record<ErrorType, string> = {
  [ErrorType.NETWORK]: 'Network connection error. Please check your internet connection and try again.',
  [ErrorType.VALIDATION]: 'Please check your input and try again.',
  [ErrorType.AUTHENTICATION]: 'Authentication failed. Please log in again.',
  [ErrorType.AUTHORIZATION]: 'You don\'t have permission to perform this action.',
  [ErrorType.NOT_FOUND]: 'The requested resource was not found.',
  [ErrorType.SERVER_ERROR]: 'Server error occurred. Please try again later.',
  [ErrorType.CLIENT_ERROR]: 'An error occurred. Please try again.',
  [ErrorType.UNKNOWN]: 'An unexpected error occurred. Please try again.',
};